/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  STAFFING_DEFAULT_FILTERS,
  EMPTY_REQUEST_ROW,
  FILTER_ASSIGNED_TO_ME,
  MAX_CLIENT_RESULTS,
} from '../../../../Constants/strings';
import { format as formatDateUtil } from 'date-fns';
import { useAuth } from '../../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../../Constants/modules';
import normalizeTableUtilityResponse from '../../../../Utils/tableUtilityResponse';
import { exportToCsv } from '../../../../Utils/staffingRequestFormatters';
import { validateProjectStatusForRequest,
  validateRequestRow } from '../../../../Utils/staffingRequestValidators';
import { approveRequest,
  bulkApproveRequest,
  bulkCloseRequest,
  closeRequest,
  createRequest,
  deleteRequest,
  fetchMetaData,
  fetchRoleExperienceMappings,
  fetchRequests } from '../../../../Api/request/staffingRequest';

const normalizeOption = (list = [], labelKey = 'label', valueKey = 'value') =>
  list.map((item) => ({
    ...item,
    label: item[labelKey] ?? item.label,
    value: item[valueKey] ?? item.value,
  }));

const splitNames = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const buildApproverOptions = (list = []) => {
  const normalizedList = Array.isArray(list) ? list : [];
  const uniqueNames = Array.from(new Set(
    normalizedList.flatMap((item) => splitNames(item?.approvers || item?.access_employee_name || '')),
  ));

  return [
    { label: 'Assigned to Me', value: FILTER_ASSIGNED_TO_ME },
    ...uniqueNames.map((name) => ({ label: name, value: name })),
  ];
};

const updateProjectOptions = (projects = [], verticals = []) => {
  const visibleProjects = verticals.length
    ? projects.filter((project) => verticals.includes(project.project_vertical || project.vertical))
    : projects;

  return normalizeOption(visibleProjects, 'project_name', 'project_id');
};

const getSelectedProject = (projects = [], projectIds = [], list = []) => {
  const projectId = projectIds.length === 1 ? projectIds[0] : list[0]?.project_id;

  if (!projectId) return null;

  return projects.find((project) => String(project.project_id) === String(projectId)) || null;
};

const getMetaWithFallback = (payload = {}) => {
  const verticals = payload.verticals || [];
  const projects = payload.projects || [];
  const roles = payload.roles || [];

  return {
    verticals,
    projects,
    roles,
  };
};

const extractRoleExperienceMappings = (response = {}) => {
  const candidates = [
    response?.data?.roleExperienceMappings,
    response?.data?.data?.roleExperienceMappings,
    response?.roleExperienceMappings,
    response?.data?.data,
    response?.data,
  ];

  return candidates.find(Array.isArray) || [];
};

const normalizeRows = (list = []) => list.map((row) => ({
  ...row,
  approvers: row.approvers || row.access_employee_name || '',
}));

const formatDateString = () => formatDateUtil(new Date(), 'yyyy-MM-dd');

const getApprovalDecisionMeta = (status) => {
  const numericStatus = Number(status);
  return numericStatus === 1
    ? { verb: 'approve', label: 'approved' }
    : { verb: 'reject', label: 'rejected' };
};

const getApiErrorMessage = (payload, fallbackMessage) => {
  if (!payload) return fallbackMessage;
  if (payload instanceof Error && payload.message) return payload.message;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload?.response?.data?.message === 'string' && payload.response.data.message.trim()) {
    return payload.response.data.message;
  }
  return fallbackMessage;
};

const mapAppliedFiltersToUiFilters = (appliedFilters = {}) => {
  const projectIds = Array.isArray(appliedFilters.project_id)
    ? appliedFilters.project_id
    : (appliedFilters.project_id ? [appliedFilters.project_id] : []);
  const verticals = Array.isArray(appliedFilters.vertical)
    ? appliedFilters.vertical
    : (appliedFilters.vertical ? [appliedFilters.vertical] : []);
  const approvalStatus = Array.isArray(appliedFilters.is_approved)
    ? appliedFilters.is_approved
    : (appliedFilters.is_approved !== undefined ? [appliedFilters.is_approved] : []);
  const requestStatus = Array.isArray(appliedFilters.is_close)
    ? appliedFilters.is_close
    : (appliedFilters.is_close !== undefined ? [appliedFilters.is_close] : []);
  const resourceTypeIds = Array.isArray(appliedFilters.resource_type_id)
    ? appliedFilters.resource_type_id
    : (appliedFilters.resource_type_id ? [appliedFilters.resource_type_id] : []);
  const approverNames = Array.isArray(appliedFilters.approved_by)
    ? appliedFilters.approved_by
    : (appliedFilters.approved_by ? [appliedFilters.approved_by] : []);
  const approvers = [
    ...(appliedFilters.assigned_to_me ? [FILTER_ASSIGNED_TO_ME] : []),
    ...approverNames,
  ];

  return {
    ...STAFFING_DEFAULT_FILTERS,
    projectIds,
    verticals,
    approvalStatus,
    requestStatus,
    resourceTypeIds,
    approvers,
    requestDateFrom: appliedFilters.date_type === 'request_date' ? appliedFilters.from || '' : '',
    requestDateTo: appliedFilters.date_type === 'request_date' ? appliedFilters.to || '' : '',
    approvalDateFrom: appliedFilters.date_type === 'approval_date' ? appliedFilters.from || '' : '',
    approvalDateTo: appliedFilters.date_type === 'approval_date' ? appliedFilters.to || '' : '',
    closedDateFrom: appliedFilters.date_type === 'closed_date' ? appliedFilters.from || '' : '',
    closedDateTo: appliedFilters.date_type === 'closed_date' ? appliedFilters.to || '' : '',
  };
};

const filterLocalRequests = (list = [], searchFilters = {}, currentUserName = '') => {
  if (!list || !list.length) return [];

  const {
    projectIds = [],
    verticals = [],
    approvalStatus = [],
    requestStatus = [],
    resourceTypeIds = [],
    approvers = [],
    searchText = '',
    requestDateFrom = '',
    requestDateTo = '',
    approvalDateFrom = '',
    approvalDateTo = '',
    closedDateFrom = '',
    closedDateTo = '',
  } = searchFilters;

  const isWithinRange = (value, from, to) => {
    if (!from && !to) return true;
    if (!value) return false;

    const normalizedValue = new Date(value);
    if (Number.isNaN(normalizedValue.getTime())) return false;

    if (from) {
      const normalizedFrom = new Date(from);
      if (!Number.isNaN(normalizedFrom.getTime()) && normalizedValue < normalizedFrom) return false;
    }

    if (to) {
      const normalizedTo = new Date(to);
      if (!Number.isNaN(normalizedTo.getTime()) && normalizedValue > normalizedTo) return false;
    }

    return true;
  };

  return list.filter((row) => {
    const projectIdsStr = projectIds.map((projectId) => String(projectId));
    const verticalsStr = verticals.map((vertical) => String(vertical).toLowerCase());
    const approvalStatusStr = approvalStatus.map((status) => String(status));
    const resourceTypeIdsStr = resourceTypeIds.map((resourceId) => String(resourceId));
    const approversStr = approvers
      .filter((approver) => String(approver) !== FILTER_ASSIGNED_TO_ME)
      .map((approver) => String(approver).toLowerCase());
    const requiresAssignedToMe = approvers.some(
      (approver) => String(approver) === FILTER_ASSIGNED_TO_ME,
    );
    const rowApproverValues = splitNames(
      row.approved_by || row.approvers || row.access_employee_name || '',
    ).map((name) => String(name).toLowerCase());

    if (projectIdsStr.length && !projectIdsStr.includes(String(row.project_id))) return false;
    const rowVertical = String(row.vertical || row.project_vertical || '').toLowerCase();
    if (verticalsStr.length && !verticalsStr.includes(rowVertical)) return false;
    if (approvalStatusStr.length && !approvalStatusStr.includes(String(row.is_approved))) return false;
    if (resourceTypeIdsStr.length && !resourceTypeIdsStr.includes(String(row.resource_type_id))) return false;
    if (approversStr.length && !approversStr.some((approver) => rowApproverValues.includes(approver))) return false;
    if (requiresAssignedToMe && !rowApproverValues.includes(String(currentUserName || '').toLowerCase())) return false;
    if (requestStatus.length) {
      const requestStatusStr = requestStatus.map((status) => String(status));
      if (!requestStatusStr.includes(String(row.is_close))) return false;
    }

    if (!isWithinRange(row.submit_date, requestDateFrom, requestDateTo)) return false;
    if (!isWithinRange(row.approved_date, approvalDateFrom, approvalDateTo)) return false;
    if (!isWithinRange(row.close_date, closedDateFrom, closedDateTo)) return false;
    if (searchText) {
      const text = String(searchText).toLowerCase();
      const hay = [row.project_name, row.sr_number, row.skills, row.resource_type_name, row.approvers]
        .filter(Boolean)
        .join(' ');
      const hayLower = hay.toLowerCase();
      if (!hayLower.includes(text)) return false;
    }

    return true;
  });
};

const canDeleteRowCheck = (row) => {
  const approved = Number(row?.is_approved || 0);
  const allocated = Number(row?.allocated_res || 0);
  const closed = Boolean(row?.close_date);
  return approved === 0 && allocated === 0 && !closed;
};

const hasMeaningfulAppliedFilters = (appliedFilters = null) => {
  if (!appliedFilters || typeof appliedFilters !== 'object') return false;

  return Object.values(appliedFilters).some((value) => (
    Array.isArray(value)
      ? value.length > 0
      : value !== undefined
        && value !== null
        && value !== ''
  ));
};

const getProjectIdsFromTableConfiguration = (tableConfiguration = []) => {
  const columns = Array.isArray(tableConfiguration) ? tableConfiguration : [];

  for (const column of columns) {
    const menuItems = Array.isArray(column?.menuItems) ? column.menuItems : [];
    for (const menuItem of menuItems) {
      const items = Array.isArray(menuItem?.items) ? menuItem.items : [];
      const projectFilter = items.find((item) => item?.field === 'project_id');

      if (projectFilter) {
        return Array.isArray(projectFilter.selectedValues)
          ? projectFilter.selectedValues
          : [];
      }
    }
  }

  return null;
};

// eslint-disable-next-line max-lines-per-function
const useStaffingRequest = () => {
  const [filters, setFilters] = useState(STAFFING_DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(STAFFING_DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [tableConfiguration, setTableConfiguration] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [requestCount, setRequestCount] = useState(0);
  const tableResetToken = 0;

  const [allProjects, setAllProjects] = useState([]);
  const [options, setOptions] = useState({
    verticals: [],
    projects: [],
    roles: [],
    approvers: [],
    roleExperienceMappings: [],
  });
  const [metaLoaded, setMetaLoaded] = useState(false);

  const { userProfile } = useAuth();
  const permissionList = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.DELIVERY_ADD_STAFFING_REQUEST] || []
    : [];
  const approvalPermissionList = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.DELIVERY_ADD_STAFFING_REQUEST_APPROVAL] || []
    : [];

  const hasAccess = (action) => (
    Array.isArray(permissionList) && permissionList.includes(action)
  );
  const hasApprovalAccess = (action) => (
    Array.isArray(approvalPermissionList) && approvalPermissionList.includes(action)
  );

  const permission = (() => {
    const perms = userProfile?.permissions || {};

    const modulePerm = permissionList;
    const approvalModulePerm = approvalPermissionList;

    const hasModuleAction = (action) => (
      Array.isArray(modulePerm) && modulePerm.includes(action)
    );
    const hasApprovalModuleAction = (action) => (
      Array.isArray(approvalModulePerm) && approvalModulePerm.includes(action)
    );
    const egmPerms = perms[MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_EGM] || [];
    const canViewEgm = Array.isArray(egmPerms) && (egmPerms.includes('read') || hasApprovalModuleAction('view'));

    return {
      canView: hasModuleAction('read') || hasApprovalModuleAction('view'),
      canApprove: hasApprovalModuleAction('update') || hasApprovalModuleAction('edit'),
      canClose: hasApprovalModuleAction('update') || hasApprovalModuleAction('edit'),
      canDelete: hasModuleAction('delete') || hasApprovalModuleAction('remove'),
      canViewEgm: canViewEgm,
      userName: userProfile?.name || userProfile?.user_name || '',
      currentUser: userProfile?.employeeId || userProfile?.emp_id || '',
    };
  })();

  // eslint-disable-next-line no-console
  const [projectInfo, setProjectInfo] = useState({
    project_name: '',
    project_status: '',
    project_type_id: null,
    start_date: '',
    replanned_end_date: '',
    estimated_egm: 0,
  });

  const [selectedRequestIds, setSelectedRequestIds] = useState([]);

  const [activeRequest, setActiveRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [approveDefaultStatus, setApproveDefaultStatus] = useState('');
  const [closeDefaultStatus, setCloseDefaultStatus] = useState('');
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkCloseModal, setShowBulkCloseModal] = useState(false);

  const [showNewRequest, setShowNewRequest] = useState(false);
  const [draftRow, setDraftRow] = useState(EMPTY_REQUEST_ROW);
  const [localRequests, setLocalRequests] = useState([]);
  const [deletingIds, setDeletingIds] = useState([]);
  const [newRequestError, setNewRequestError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const updateDraftRow = useCallback((nextDraftRow) => {
    setDraftRow(nextDraftRow);
    if (newRequestError) setNewRequestError('');
  }, [newRequestError]);

  const loadRoleExperienceMappings = useCallback(async () => {
    try {
      const response = await fetchRoleExperienceMappings(Date.now());
      const nextRoleExperienceMappings = extractRoleExperienceMappings(response);
      if (!nextRoleExperienceMappings.length) return;

      setOptions((state) => ({
        ...state,
        roleExperienceMappings: nextRoleExperienceMappings,
      }));
    } catch (err) {
      // Keep Staffing Request usable with existing metadata if mapping reload fails.
    }
  }, []);

  useEffect(() => {
    loadRoleExperienceMappings();
  }, [loadRoleExperienceMappings]);

  const setFilterValue = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };

      if (key === 'verticals') {
        next.projectIds = [];
        setOptions((state) => ({
          ...state,
          projects: updateProjectOptions(allProjects, value),
        }));
      }

      return next;
    });
  };

  const fetchMeta = useCallback(async () => {
    try {
      const [response, roleExperienceMappingsResponse] = await Promise.all([
        fetchMetaData(),
        fetchRoleExperienceMappings(Date.now()).catch(() => ({ data: { roleExperienceMappings: [] } })),
      ]);
      const payload = response?.data?.data || response?.data || {};
      const meta = getMetaWithFallback(payload);
      const nextRoleExperienceMappings = extractRoleExperienceMappings(roleExperienceMappingsResponse);

      setAllProjects(meta.projects);
      setOptions({
        verticals: normalizeOption(meta.verticals, 'vertical', 'vertical'),
        projects: normalizeOption(meta.projects, 'project_name', 'project_id'),
        roles: normalizeOption(meta.roles, 'resource_type', 'type_id'),
        approvers: [],
        roleExperienceMappings: nextRoleExperienceMappings,
      });

      // Debug: log meta load
      // eslint-disable-next-line no-console
      // Do not consume permission flags from backend meta; permissions are derived from app-level user profile
      setMetaLoaded(true);
      return meta;
    } catch (err) {
      setErrorMessage('Failed to load metadata');
      setMetaLoaded(false);
      return null;
    }
  }, []);

  const applyRowsResponse = useCallback((response, searchFilters = filters) => {
    const normalizedResponse = normalizeTableUtilityResponse(response);
    const respData = normalizedResponse?.data || {};

    const filterOptions = normalizedResponse?.meta?.filterOptions || respData?.meta?.filterOptions || null;
    if (filterOptions) {
      const meta = getMetaWithFallback(filterOptions);
      const rawApprovers = Array.isArray(filterOptions.approvers) ? filterOptions.approvers : [];
      setAllProjects(meta.projects);
      setOptions((prev) => ({
        ...prev,
        verticals: normalizeOption(meta.verticals, 'vertical', 'vertical'),
        projects: normalizeOption(meta.projects, 'project_name', 'project_id'),
        roles: normalizeOption(meta.roles, 'resource_type', 'type_id'),
        approvers: rawApprovers.map((approver) => ({ label: approver.approved_by, value: approver.approved_by })),
        roleExperienceMappings: prev.roleExperienceMappings || [],
      }));
      setMetaLoaded(true);
    }

    const responseTableConfiguration = Array.isArray(respData?.tableConfiguration)
      ? respData.tableConfiguration
      : Array.isArray(respData?.data?.tableConfiguration)
        ? respData.data.tableConfiguration
        : [];
    const projectIdsFromTableConfiguration = getProjectIdsFromTableConfiguration(responseTableConfiguration);
    const appliedFiltersFromResponse = respData?.data?.appliedFilters ?? respData?.appliedFilters;
    const responseHasAppliedFiltersPayload = appliedFiltersFromResponse !== undefined
      && appliedFiltersFromResponse !== null;
    const responseHasAppliedFilters = hasMeaningfulAppliedFilters(appliedFiltersFromResponse);
    const responseFilters = responseHasAppliedFiltersPayload
      ? mapAppliedFiltersToUiFilters(appliedFiltersFromResponse)
      : (
        projectIdsFromTableConfiguration !== null
          ? { ...searchFilters, projectIds: projectIdsFromTableConfiguration }
          : null
      );
    const effectiveFilters = responseFilters
      ? responseFilters
      : searchFilters;
    const list = Array.isArray(respData?.tableData)
      ? respData.tableData
      : Array.isArray(respData?.data?.tableData)
        ? respData.data.tableData
        : [];
    const currentUserName = userProfile?.name || userProfile?.user_name || userProfile?.employeeId || '';
    const filteredServerRequests = Array.isArray(list) ? list : [];
    const filteredLocalRequests = filterLocalRequests(localRequests, effectiveFilters, currentUserName);
    const mergedList = [...filteredLocalRequests, ...filteredServerRequests];

    setTableConfiguration(responseTableConfiguration);
    setRows(normalizeRows(mergedList));
    setIsLoading(false);
    setRequestCount(normalizedResponse?.meta?.total || respData?.meta?.total || mergedList.length || 0);

    setOptions((state) => ({
      ...state,
      projects: updateProjectOptions(allProjects, effectiveFilters.verticals || []),
      approvers: buildApproverOptions(mergedList),
    }));

    if (responseHasAppliedFiltersPayload || responseHasAppliedFilters || projectIdsFromTableConfiguration !== null) {
      setFilters(effectiveFilters);
      setAppliedFilters(effectiveFilters);
    }

    const selectedProject = getSelectedProject(
      allProjects,
      effectiveFilters.projectIds || [],
      mergedList,
    ) || normalizedResponse?.data?.meta?.projectInfo || null;

    if (selectedProject) {
      setProjectInfo({
        project_name: selectedProject.project_name || '',
        project_status: selectedProject.project_status || '',
        project_type_id: selectedProject.project_type_id || null,
        start_date: selectedProject.start_date || '',
        replanned_end_date: selectedProject.replanned_end_date || '',
        estimated_egm: selectedProject.gm_percent || 0,
      });
    } else {
      setProjectInfo({
        project_name: '',
        project_status: '',
        project_type_id: null,
        start_date: '',
        replanned_end_date: '',
        estimated_egm: 0,
      });
    }
    return normalizedResponse;
  }, [allProjects, filters, localRequests, userProfile?.employeeId, userProfile?.name, userProfile?.user_name]);

  const fetchRows = useCallback(async (searchFilters = filters) => {
    setAppliedFilters({ ...searchFilters });
    setIsLoading(true);
    setErrorMessage('');

    try {
      const listResponse = await fetchRequests(searchFilters);
      applyRowsResponse(listResponse, searchFilters);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
      }
    } finally {
      setIsLoading(false);
      setSelectedRequestIds([]);
    }
  }, [applyRowsResponse, filters]);

  const handleTableUtilityResponse = useCallback((response) => {
    const normalizedResponse = applyRowsResponse(response, filters);
    setErrorMessage('');
    setAlertMessage('');
    setSelectedRequestIds([]);
    return normalizedResponse;
  }, [applyRowsResponse, filters]);

  const toggleSelectRow = (requestId) => {
    setSelectedRequestIds((prev) => (
      prev.includes(requestId)
        ? prev.filter((id) => id !== requestId)
        : [...prev, requestId]
    ));
  };

  // open approve modal and optionally set a default status (e.g. auto-select Approved)
  const openApproveModalWithDefault = (request, defaultStatus = '') => {
    if ((!hasApprovalAccess('update') && !hasApprovalAccess('edit'))) return;
    setActiveRequest(request);
    setApproveDefaultStatus(defaultStatus);
    setShowApproveModal(true);
  };

  // open close modal and optionally set a default status (e.g. auto-select Closed)
  const openCloseModalWithDefault = (request, defaultStatus = '') => {
    if ((!hasApprovalAccess('update') && !hasApprovalAccess('edit'))) return;
    setActiveRequest(request);
    setCloseDefaultStatus(defaultStatus);
    setShowCloseModal(true);
  };

  const closeModals = () => {
    setShowApproveModal(false);
    setShowCloseModal(false);
    setShowBulkApproveModal(false);
    setShowBulkCloseModal(false);
    setActiveRequest(null);
  };

  const submitApprove = async ({ status, remarks }) => {
    if (!activeRequest) return;
    const decisionMeta = getApprovalDecisionMeta(status);
    const employeeId = userProfile?.employeeId || userProfile?.emp_id || userProfile?.empId || '';
    const emailId = userProfile?.emailId || userProfile?.email || '';

    try {
      await approveRequest({
        requestId: activeRequest.request_id,
        status,
        remarks,
        approved_id: employeeId,
        approved_by: userProfile?.name || userProfile?.user_name || '',
        userInfo: {
          employeeId,
          emailId,
        },
      });
      closeModals();
      await fetchRows(appliedFilters);
      toast.success(`Request ${decisionMeta.label} successfully`);
    } catch (err) {
      const message = getApiErrorMessage(err, `Failed to ${decisionMeta.verb} request`);
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const submitClose = async ({ status, remarks, date }) => {
    if (!activeRequest) return;

    const closePayload = {
      status: Number(status),
      remarks,
      date: Number(status) === 1 ? (date || formatDateString()) : '',
    };

    const updateCurrentRows = () => {
      setRows((prev) => prev.map((row) => {
        if (Number(row.request_id) !== Number(activeRequest.request_id)) return row;
        return {
          ...row,
          is_close: closePayload.status === 1 ? 1 : 0,
          close_date: closePayload.date,
          close_remarks: remarks,
        };
      }));
    };

    if (activeRequest.isLocalOnly) {
      setLocalRequests((prev) => prev.map((row) => {
        if (Number(row.request_id) !== Number(activeRequest.request_id)) return row;
        return {
          ...row,
          is_close: closePayload.status === 1 ? 1 : 0,
          close_date: closePayload.date,
          close_remarks: remarks,
        };
      }));
      updateCurrentRows();
      closeModals();
      return;
    }

    try {
      await closeRequest({
        srNumber: activeRequest.sr_number,
        requestId: activeRequest.request_id,
        status,
        closeDate: closePayload.date,
        remarks,
      });

      updateCurrentRows();
      closeModals();
    } catch (err) {
      setErrorMessage('Failed to close request');
    }
  };

  const submitBulkApprove = async ({ status, remarks }) => {
    if (!selectedRequestIds.length) return;
    const decisionMeta = getApprovalDecisionMeta(status);
    const employeeId = userProfile?.employeeId || userProfile?.emp_id || userProfile?.empId || '';
    const emailId = userProfile?.emailId || userProfile?.email || '';

    try {
      await bulkApproveRequest({
        ids: selectedRequestIds,
        is_approved: Number(status),
        approved_remarks: remarks,
        approved_id: employeeId,
        approved_by: userProfile?.name || userProfile?.user_name || '',
        userInfo: {
          employeeId,
          emailId,
        },
      });
      closeModals();
      setSelectedRequestIds([]);
      await fetchRows(appliedFilters);
      toast.success(`Selected requests ${decisionMeta.label} successfully`);
    } catch (err) {
      const message = getApiErrorMessage(err, `Failed to ${decisionMeta.verb} selected requests`);
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const submitBulkClose = async ({ status, remarks, date }) => {
    if (!selectedRequestIds.length) return;

    const selectedIdSet = new Set(selectedRequestIds.map((requestId) => Number(requestId)));

    const closePayload = {
      status: Number(status),
      remarks,
      date: Number(status) === 1 ? (date || formatDateString()) : '',
    };

    const updateCurrentRows = () => {
      setRows((prev) => prev.map((row) => {
        if (!selectedIdSet.has(Number(row.request_id))) return row;
        return {
          ...row,
          is_close: closePayload.status === 1 ? 1 : 0,
          close_date: closePayload.date,
          close_remarks: remarks,
        };
      }));
    };

    setLocalRequests((prev) => prev.map((row) => {
      if (!selectedIdSet.has(Number(row.request_id))) return row;
      return {
        ...row,
        is_close: closePayload.status === 1 ? 1 : 0,
        close_date: closePayload.date,
        close_remarks: remarks,
      };
    }));

    try {
      await bulkCloseRequest({
        ids: selectedRequestIds,
        is_close: Number(status),
        close_date: closePayload.date,
        close_remarks: remarks,
      });

      updateCurrentRows();
      closeModals();
      setSelectedRequestIds([]);
    } catch (err) {
      setErrorMessage('Failed to bulk close requests');
    }
  };

  const removeRequest = async (request) => {
    const reason = getDeleteReason(request);
    if (reason) {
      toast.error(reason);
      return;
    }

    if (request.isLocalOnly) {
      setLocalRequests((prev) => prev.filter((row) => row.request_id !== request.request_id));
      setRows((prev) => prev.filter((row) => row.request_id !== request.request_id));
      setRequestCount((prev) => Math.max(prev - 1, 0));
      return;
    }

    try {
      setDeletingIds((prev) => [...prev, Number(request.request_id)]);
      await deleteRequest({
        staffingId: request.request_id,
        lastUpdatedBy: userProfile?.user_name,
      });
      toast.success('Staffing request deleted');
      await fetchRows(filters);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to delete request');
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setDeletingIds((prev) => prev.filter((id) => Number(id) !== Number(request.request_id)));
    }
  };

  const getDeleteReason = (row) => {
    if (!row) return 'Invalid request';
    if (!(hasAccess('delete') || permission.canDelete)) return 'Missing delete permission';
    if (Number(row.is_approved) !== 0) return 'Cannot delete: request approved or rejected';
    if (Number(row?.allocated_res || 0) > 0) return 'Cannot delete: resources allocated';
    if (row?.close_date) return 'Cannot delete: request closed';
    return '';
  };

  const submitNewRequest = async () => {
    const statusError = validateProjectStatusForRequest(projectInfo.project_status);
    if (statusError) {
      setNewRequestError(statusError);
      return;
    }

    const error = validateRequestRow(draftRow);
    setNewRequestError(error);
    if (error) return;

    if (!draftRow.projectId) {
      setNewRequestError('Please select project.');
      return;
    }

    const project = allProjects.find((item) => String(item.project_id) === String(draftRow.projectId));

    const projectStatusError = validateProjectStatusForRequest(project?.project_status || '');
    if (projectStatusError) {
      setNewRequestError(projectStatusError);
      return;
    }

    if (!project) {
      setNewRequestError('Select a project before adding staffing requests.');
      return;
    }

    const resolvedProjectId = Number(draftRow.projectId || project.project_id);
    if (Number.isNaN(resolvedProjectId) || resolvedProjectId <= 0) {
      setNewRequestError('Invalid project selected. Please select a valid project.');
      return;
    }

    const payload = {
      project_id: resolvedProjectId,
      start_date: draftRow.startDate,
      end_date: draftRow.endDate,
      skills: draftRow.mandatorySkills || '',
      remarks: draftRow.remarks || '',
      resource_no: Number(draftRow.resourceNo) || 1,
      allocation_per: Number(draftRow.allocation) || 100,
      resource_type_id: draftRow.roleId || null,
      vertical: draftRow.vertical || project.project_vertical,
      experience: draftRow.experience || '',
      good_skills: draftRow.goodSkills || '',
      submit_id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.empId || '',
      submit_by: userProfile?.name || userProfile?.user_name || '',
      userInfo: {
        employeeId: userProfile?.employeeId || userProfile?.emp_id || userProfile?.empId || '',
        emailId: userProfile?.emailId || userProfile?.email || '',
      },
    };

    setIsCreating(true);
    try {
      const response = await createRequest(payload);
      const responseData = response?.data;

      if (!responseData || responseData?.name === 'AxiosError' || responseData?.isAxiosError) {
        throw responseData || new Error('Failed to create staffing request.');
      }

      if (typeof responseData === 'object' && responseData !== null && responseData.success === false) {
        throw new Error(responseData.message || 'Failed to create staffing request.');
      }

      toast.success(responseData?.message || 'Staffing request created');
      await fetchRows(appliedFilters);
      setDraftRow(EMPTY_REQUEST_ROW);
      setNewRequestError('');
      setShowNewRequest(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create staffing request.'));
    } finally {
      setIsCreating(false);
    }
  };

  const canSelectRows = useMemo(() => {
    if (filters.requestStatus.length === 1 && Number(filters.requestStatus[0]) === 1) return false;
    if (filters.approvalStatus.length !== 1) return false;

    const status = Number(filters.approvalStatus[0]);
    return (
      (status === 0 && (
        hasApprovalAccess('update')
        || permission.canApprove
      ))
      || ((status === 1 || status === 2) && (
        hasApprovalAccess('update')
        || permission.canClose
      ))
    );
  }, [
    filters.approvalStatus,
    filters.requestStatus,
    hasAccess,
    hasApprovalAccess,
    permission.canApprove,
    permission.canClose,
  ]);

  const canOpenNewRequest = appliedFilters.projectIds.length === 1
    && (hasAccess('create') || permission.canView);

  const canDeleteRow = (row) => (
    (hasAccess('delete') || permission.canDelete) && canDeleteRowCheck(row)
  );

  const exportRows = () => exportToCsv(rows);

  const tooManyResults = requestCount > MAX_CLIENT_RESULTS;

  return {
    filters,
    rows,
    options,
    metaLoaded,
    requestCount,
    isLoading,
    alertMessage,
    errorMessage,
    selectedRequestIds,
    projectInfo,
    permission,
    permissionList,
    approvalPermissionList,
    canSelectRows,
    canOpenNewRequest,
    canDeleteRow,
    tooManyResults,
    showApproveModal,
    showCloseModal,
    showBulkApproveModal,
    showBulkCloseModal,
    showNewRequest,
    activeRequest,
    draftRow,
    newRequestError,
    setFilterValue,
    setShowBulkApproveModal,
    setShowBulkCloseModal,
    setShowNewRequest,
    setDraftRow: updateDraftRow,
    fetchMeta,
    fetchRows,
    handleTableUtilityResponse,
    tableConfiguration,
    toggleSelectRow,
    openApproveModalWithDefault,
    approveDefaultStatus,
    openCloseModalWithDefault,
    closeDefaultStatus,
    closeModals,
    submitApprove,
    submitClose,
    submitBulkApprove,
    submitBulkClose,
    removeRequest,
    submitNewRequest,
    exportRows,
    isCreating,
    getDeleteReason,
    deletingIds,
    hasAccess,
    hasApprovalAccess,
    userProfile,
    tableResetToken,
  };
};

export default useStaffingRequest;
